import { DataBoxTable } from '../../../../Components/DataBoxTable/DataBoxTable';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { DetailsJobSchema, PriorityJobSchema } from '../../../../Utils/schemas';
import { useEffect, useState } from 'react';
import { DataBox } from '../../../../Components/DataBox/DataBox';
import { Paper, TextField, Typography } from '@mui/material';
import { priorityAndConfidentialityFields } from '../../../../Utils/fields';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { checkJobReferenceExists, editJobSkills, getJobSkills } from '../../../../Services/Jobs';
import { JobStatus } from '../../../../Types/constants';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import SearchableChipBox from '../../../../Components/SearchableChipBox/SearchableChipBox';
import { GetDetailsFields } from './fields';
import { Loader } from '../../../../Components/Loader/Loader';
import { getClients } from '../../../../Services/Client';
import { getJobDepartments } from '../../../../Services/jobDepartment';

export const JobSummary = ({ job, updateJob }: { job: Job; updateJob: any }) => {
  const { t } = useTranslation();
  const jobDetailsTitle = t('jobs.details.title');
  const jobDescriptionTitle = t('jobs.details.description_title');
  const jobPriorityAndConfidentialityTitle = t('jobs.details.priority_and_confidentiality_title');
  const queryClient = useQueryClient();
  const [description, setDescription] = useState(job.description ?? '');
  const [onDetailsEdit, setOnDetailsEdit] = useState(false);
  const [onDescriptionEdit, setOnDescriptionEdit] = useState(false);
  const [onPriorityAndConfidentialityEdit, setOnPriorityAndConfidentialityEdit] = useState(false);
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [detailFields, setDetailFields] = useState<Field[]>([]);
  const [isValidatingJobReference, setIsValidatingJobReference] = useState(false);

  const ValidatedDetailsJobSchema = DetailsJobSchema.refine(
    (data) => data.jobReference && data.jobReference.trim() !== '',
    {
      message: t('jobs.summary.error_job_reference_required'),
      path: ['jobReference'],
    }
  ).refine(
    (data) => {
      if (data.minimumSalary && data.maximumSalary) {
        const minSalary = parseFloat(data.minimumSalary);
        const maxSalary = parseFloat(data.maximumSalary);
        return minSalary < maxSalary;
      }
      return true;
    },
    {
      message: t('jobs.summary.error_minimumSalary'),
      path: ['minimumSalary'],
    }
  ).refine(
    (data) => {
      if (data.minimumSalary && data.maximumSalary) {
        const minSalary = parseFloat(data.minimumSalary);
        const maxSalary = parseFloat(data.maximumSalary);
        return maxSalary > minSalary;
      }
      return true;
    },
    {
      message: t('jobs.summary.error_maximumSalary'),
      path: ['maximumSalary'],
    }
  ).refine(
    (data) => {
      if ((data.minimumSalary || data.maximumSalary) && !data.currency) {
        return false;
      }
      return true;
    },
    {
      message: t('jobs.summary.error_currency'),
      path: ['currency'],
    }
  );

  const defaultFormValues = {
    position: job.position || '',
    jobReference: job.jobReference || '',
    headcount: job.headcount || '',
    departmentId: job.departmentId ?? '',
    department: job.jobDepartment ?? '',
    client: job.client || '',
    closeDateTime: job.closeDateTime ?? '',
    requester: job.requester ?? '',
    techHub: job.techHub ?? '',
    seniority: job.seniority || '',
    minimumSalary: job.minimumSalary ?? '',
    maximumSalary: job.maximumSalary ?? '',
    currency: job.currency ?? '',
    expectedStartDateTime: job.expectedStartDateTime ?? '',
    experience: job.experience ?? '',
    characteristics: job.characteristics ?? '',
    market: job.market ?? '',
    modality: job.modality ?? '',
    id: `${job.id}`,
  };

  const {
    control: controlDetails,
    handleSubmit: handleSubmitDetails,
    formState: { errors: errorsDetails },
    setError,
    clearErrors,
  } = useForm<z.infer<typeof ValidatedDetailsJobSchema>>({
    resolver: zodResolver(ValidatedDetailsJobSchema),
    defaultValues: defaultFormValues,
  });

  const {
    control: controlPriority,
    handleSubmit: handleSubmitPriority,
    formState: { errors: errorsPriority },
  } = useForm<z.infer<typeof PriorityJobSchema>>({
    resolver: zodResolver(PriorityJobSchema),
    defaultValues: {
      confidential: job.confidential,
      priority: job.priority,
    },
  });

  const onSubmitPriority: SubmitHandler<z.infer<typeof PriorityJobSchema>> = async (data) => {
    updateJob(data);
    setOnPriorityAndConfidentialityEdit(false);
  };

  const validateJobReference = async (jobReference: string): Promise<boolean> => {
    if (!jobReference || jobReference === job.jobReference) return true;

    setIsValidatingJobReference(true);
    try {
      const exists = await checkJobReferenceExists(jobReference);
      if (exists) {
        setError('jobReference', {
          type: 'manual',
          message: t('jobs.summary.error_job_reference_exists'),
        });
        setIsValidatingJobReference(false);
        return false;
      }
      clearErrors('jobReference');
      setIsValidatingJobReference(false);
      return true;
    } catch (error) {
      setIsValidatingJobReference(false);
      toast.error(t('toast.errors.something_went_wrong'));
      return false;
    }
  };

  const onSubmitDetail: SubmitHandler<z.infer<typeof ValidatedDetailsJobSchema>> = async (data) => {
    if (data.jobReference !== job.jobReference) {
      const isValid = await validateJobReference(data.jobReference);
      if (!isValid) return;
    }

    const updatedData = {
      ...data,
    };
    updateJob(updatedData);
    setOnDetailsEdit(false);
  };

  const handleSaveDescription = () => {
    updateJob({ description });
    setOnDescriptionEdit(false);
  };

  const { data: jobSkills } = useQuery({
    queryKey: ['getJobSkills', job.id],
    queryFn: () => getJobSkills(job.id),
  });

  useEffect(() => {
    if (jobSkills) {
      setSelectedSkills(jobSkills.skills);
    }
  }, [jobSkills]);

  const { mutate: editSkillsMutation } = useMutation({
    mutationFn: (skills: string[]) => editJobSkills('' + job.id, skills),
    onSuccess: () => {
      toast.success(t('toast.success.edit', { elem: 'Skills' }));
      queryClient.invalidateQueries({ queryKey: ['getJobSkills', '' + job.id] });
    },
    onError: () => {
      toast.error(t('toast.errors.something_went_wrong'));
    },
  });

  const { data: clientsResponse, isLoading } = useQuery({
    queryKey: ['getClients', job.id],
    queryFn: () =>
      getClients({
        page: 1,
        count: 1000,
      }),
  });

  const { data: jobDepartmentsResponse, isLoading: isLoadingJobDepartments } = useQuery({
    queryKey: ['getJobDepartments', job.id],
    queryFn: () =>
      getJobDepartments(),
  });

  const canEditJob = () => job.status !== JobStatus.Closed && job.status !== JobStatus.Canceled;

  useEffect(() => {
    if (!isLoading && !isLoadingJobDepartments && clientsResponse?.items) {
      const fields = GetDetailsFields(clientsResponse.items, jobDepartmentsResponse ?? []);
      setDetailFields(fields);
    }
  }, [isLoading, clientsResponse]);

  return (
    <div style={{ display: 'flex', flexDirection: 'row', alignContent: 'space-between', width: '100%' }}>
      <form onSubmit={handleSubmitDetails(onSubmitDetail)} style={{ width: '100%' }}>
        {isLoading ? (
          <Loader isLoading={isLoading} />
        ) : (
          <DataBoxTable
            title={jobDetailsTitle}
            fields={detailFields ?? []}
            control={controlDetails}
            values={job}
            handleSave={() => handleSubmitDetails(onSubmitDetail)()}
            handleEdit={canEditJob() ? () => setOnDetailsEdit(true) : undefined}
            onEdit={onDetailsEdit}
            handleCancel={() => setOnDetailsEdit(false)}
            errors={errorsDetails}
            fullWidth
          />
        )}
      </form>
      <div style={{ display: 'flex', flexDirection: 'column', width: '100%', marginLeft: '20px' }}>
        <DataBox
          title={jobDescriptionTitle}
          handleEdit={canEditJob() ? () => setOnDescriptionEdit(true) : undefined}
          handleCancel={() => setOnDescriptionEdit(false)}
          handleSave={handleSaveDescription}
          onEdit={onDescriptionEdit}
          fullWidth
        >
          <Paper style={{ padding: 20, display: 'flex', flexDirection: 'column' }}>
            {onDescriptionEdit ? (
              <TextField
                multiline
                sx={{ marginTop: '15px', '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: '#000000DE' } }}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            ) : (
              <Typography style={{ flex: 1, whiteSpace: 'pre-wrap' }}>{description}</Typography>
            )}
          </Paper>
        </DataBox>
        <form onSubmit={handleSubmitPriority(onSubmitPriority)}>
          <DataBoxTable
            fullWidth
            title={jobPriorityAndConfidentialityTitle}
            fields={priorityAndConfidentialityFields}
            control={controlPriority}
            values={{ confidential: job.confidential, priority: job.priority }}
            handleSave={() => handleSubmitPriority(onSubmitPriority)()}
            handleEdit={canEditJob() ? () => setOnPriorityAndConfidentialityEdit(true) : undefined}
            onEdit={onPriorityAndConfidentialityEdit}
            handleCancel={() => setOnPriorityAndConfidentialityEdit(false)}
            errors={errorsPriority}
          />
        </form>
        <SearchableChipBox
          initialSelectedChips={selectedSkills}
          handleSave={editSkillsMutation}
          preventEdit={!canEditJob()}
          width={'100%'}
        />
      </div>
    </div>
  );
};